import { useMemo, useRef, useState } from "react";
import { v4 as createUuid } from "uuid";

import { useSelectTheme } from "../../store";
import { ITaskSchedule } from "../../shared/db";
import LabfrontIcon from "../../asset/image/labfront_icon.png";
import { BellIcon } from "../../lib/svg/icon/bell";
import { DeleteIcon } from "../../lib/svg/icon/delete";
import { EditIcon } from "../../lib/svg/icon/edit";
import { InfoIcon } from "../../lib/svg/icon/info";
import { WarningTriangleIcon } from "../../lib/svg/icon/warning_triangle";
import {
  createDefaultTaskScheduleReminderTitle,
  createTaskScheduleReminder,
  TASK_SCHEDULE_REMINDER_MAX_COUNT,
  TASK_SCHEDULE_REMINDER_MESSAGE_MAX_LENGTH,
  TASK_SCHEDULE_REMINDER_TITLE_MAX_LENGTH,
  TaskScheduleReminderFormItem,
} from "../../model";

const minuteSeconds = 60;
const hourSeconds = 60 * minuteSeconds;

type ReminderUnit = "minutes" | "hours";

interface LocalReminderIdentity {
  reminderKey: string;
  secondsBeforeExpiration: number;
  title: string;
  message: string;
}

export interface TaskScheduleReminderListProps {
  schedule: Partial<ITaskSchedule>;
  taskName?: string;
  isViewOnly?: boolean;
  onReminderListChange: (reminderList: TaskScheduleReminderFormItem[]) => void;
}

const getReminderUnit = (seconds: number): ReminderUnit => {
  return seconds % hourSeconds === 0 && seconds >= hourSeconds ? "hours" : "minutes";
};

const getReminderValue = (seconds: number, unit: ReminderUnit): number => {
  if (unit === "hours") {
    return Math.max(1, Math.round(seconds / hourSeconds));
  }
  return Math.max(1, Math.round(seconds / minuteSeconds));
};

const unitToSeconds = (value: number, unit: ReminderUnit): number => {
  return Math.max(0, Math.round(value)) * (unit === "hours" ? hourSeconds : minuteSeconds);
};

const coerceReminderList = (
  reminderList: Partial<TaskScheduleReminderFormItem>[] | undefined,
  taskName: string,
): TaskScheduleReminderFormItem[] =>
  (reminderList ?? []).map((reminder) => {
    const secondsBeforeExpiration = reminder.secondsBeforeExpiration ?? 15 * minuteSeconds;
    return {
      ...reminder,
      secondsBeforeExpiration,
      title: reminder.title ?? createDefaultTaskScheduleReminderTitle(secondsBeforeExpiration),
      message: reminder.message ?? taskName,
    };
  });

const createLocalReminderIdentity = (
  reminder: TaskScheduleReminderFormItem,
  reminderKey = createUuid(),
): LocalReminderIdentity => ({
  reminderKey,
  secondsBeforeExpiration: reminder.secondsBeforeExpiration,
  title: reminder.title,
  message: reminder.message,
});

const localReminderIdentityMatches = (
  identity: LocalReminderIdentity,
  reminder: TaskScheduleReminderFormItem,
): boolean =>
  identity.secondsBeforeExpiration === reminder.secondsBeforeExpiration &&
  identity.title === reminder.title &&
  identity.message === reminder.message;

export const TaskScheduleReminderList = (props: TaskScheduleReminderListProps) => {
  const { schedule, taskName = "Task", isViewOnly, onReminderListChange } = props;
  const { color, fontSize, fontWeight } = useSelectTheme();
  const [expandedReminderKey, setExpandedReminderKey] = useState<string | null>(null);
  const [editingReminderTime, setEditingReminderTime] = useState<{
    key: string;
    value: string;
  } | null>(null);
  const reminderList = useMemo(
    () =>
      coerceReminderList(
        schedule.reminderList as TaskScheduleReminderFormItem[] | undefined,
        taskName,
      ),
    [schedule.reminderList, taskName],
  );
  // Reminder updates recreate server IDs, so row identity must remain local to this component.
  const reminderIdentitiesRef = useRef<LocalReminderIdentity[]>([]);
  const reminderKeys = useMemo(() => {
    const unmatchedIdentities = new Set(reminderIdentitiesRef.current);
    const nextIdentities = reminderList.map((reminder) => {
      const existingIdentity = reminderIdentitiesRef.current.find(
        (identity) =>
          unmatchedIdentities.has(identity) && localReminderIdentityMatches(identity, reminder),
      );
      if (existingIdentity) {
        unmatchedIdentities.delete(existingIdentity);
      }
      return createLocalReminderIdentity(reminder, existingIdentity?.reminderKey);
    });

    reminderIdentitiesRef.current = nextIdentities;
    return nextIdentities.map(({ reminderKey }) => reminderKey);
  }, [reminderList]);
  const reminderWithLocalKeyList = useMemo(
    () =>
      reminderList.map((reminder, index) => ({
        reminder,
        index,
        reminderKey: reminderKeys[index],
      })),
    [reminderKeys, reminderList],
  );

  const emitChange = (nextReminderList: TaskScheduleReminderFormItem[]) => {
    onReminderListChange(nextReminderList);
  };

  const replaceReminder = (
    index: number,
    updater: (reminder: TaskScheduleReminderFormItem) => TaskScheduleReminderFormItem,
  ) => {
    const nextReminderList = [...reminderList];
    nextReminderList[index] = updater(nextReminderList[index]);
    const reminderKey = reminderIdentitiesRef.current[index]?.reminderKey;
    reminderIdentitiesRef.current[index] = createLocalReminderIdentity(
      nextReminderList[index],
      reminderKey,
    );
    emitChange(nextReminderList);
  };

  const removeReminder = (index: number) => {
    const nextReminderList = reminderList.filter((_, currentIndex) => currentIndex !== index);
    const removedReminderKey = reminderIdentitiesRef.current.splice(index, 1)[0]?.reminderKey;
    if (expandedReminderKey === removedReminderKey) {
      setExpandedReminderKey(null);
    }
    if (editingReminderTime?.key === removedReminderKey) {
      setEditingReminderTime(null);
    }
    emitChange(nextReminderList);
  };

  const clampSeconds = (seconds: number, index: number): number => {
    let nextSeconds = Math.max(minuteSeconds, Math.round(seconds / minuteSeconds) * minuteSeconds);

    const usedSeconds = new Set(
      reminderList
        .filter((_, currentIndex) => currentIndex !== index)
        .map((reminder) => reminder.secondsBeforeExpiration),
    );
    while (nextSeconds > minuteSeconds && usedSeconds.has(nextSeconds)) {
      nextSeconds -= minuteSeconds;
    }
    return nextSeconds;
  };

  const changeReminderTime = (
    index: number,
    value: number,
    unit: ReminderUnit,
    shouldClamp = false,
  ) => {
    replaceReminder(index, (reminder) => {
      const previousDefaultTitle = createDefaultTaskScheduleReminderTitle(
        reminder.secondsBeforeExpiration,
      );
      const secondsBeforeExpiration = shouldClamp
        ? clampSeconds(unitToSeconds(value, unit), index)
        : unitToSeconds(value, unit);
      const nextDefaultTitle = createDefaultTaskScheduleReminderTitle(secondsBeforeExpiration);
      const shouldUpdateTitle = !reminder.title?.trim() || reminder.title === previousDefaultTitle;
      const shouldUpdateMessage = !reminder.message?.trim();

      return {
        ...reminder,
        secondsBeforeExpiration,
        title: shouldUpdateTitle ? nextDefaultTitle : reminder.title,
        message: shouldUpdateMessage ? taskName : reminder.message,
      };
    });
  };

  const changeReminderText = (index: number, field: "title" | "message", value: string) => {
    const maxLength =
      field === "title"
        ? TASK_SCHEDULE_REMINDER_TITLE_MAX_LENGTH
        : TASK_SCHEDULE_REMINDER_MESSAGE_MAX_LENGTH;
    replaceReminder(index, (reminder) => ({
      ...reminder,
      [field]: value.slice(0, maxLength),
    }));
  };

  const resetReminderText = (index: number) => {
    replaceReminder(index, (reminder) => ({
      ...reminder,
      title: createDefaultTaskScheduleReminderTitle(reminder.secondsBeforeExpiration),
      message: taskName,
    }));
  };

  const addReminder = () => {
    if (isViewOnly || reminderList.length >= TASK_SCHEDULE_REMINDER_MAX_COUNT) {
      return;
    }

    const usedSeconds = new Set(reminderList.map((reminder) => reminder.secondsBeforeExpiration));
    const candidateSeconds = [30, 10, 5, 60, 120, 45, 20, 2]
      .map((minutes) => minutes * minuteSeconds)
      .find((seconds) => !usedSeconds.has(seconds));

    let secondsBeforeExpiration = candidateSeconds ?? (reminderList.length + 1) * minuteSeconds;
    while (secondsBeforeExpiration > minuteSeconds && usedSeconds.has(secondsBeforeExpiration)) {
      secondsBeforeExpiration -= minuteSeconds;
    }

    if (secondsBeforeExpiration < minuteSeconds) {
      return;
    }

    const nextReminder = createTaskScheduleReminder({ secondsBeforeExpiration, taskName });
    reminderIdentitiesRef.current.push(createLocalReminderIdentity(nextReminder));
    emitChange([...reminderList, nextReminder]);
  };

  const getReminderError = (
    reminder: TaskScheduleReminderFormItem,
    index: number,
  ): string | null => {
    const secondsBeforeExpiration = reminder.secondsBeforeExpiration;
    if (!Number.isFinite(secondsBeforeExpiration) || secondsBeforeExpiration < minuteSeconds) {
      return "Enter a value of at least 1 minute.";
    }
    if (
      reminderList.some(
        (item, currentIndex) =>
          currentIndex !== index && item.secondsBeforeExpiration === secondsBeforeExpiration,
      )
    ) {
      return "Another reminder already fires at this time.";
    }
    return null;
  };

  const atCap = reminderList.length >= TASK_SCHEDULE_REMINDER_MAX_COUNT;
  const canAdd = !isViewOnly && !atCap;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      css={{
        width: "100%",
        marginTop: 16,
        paddingTop: 16,
        borderTop: `1px solid ${color.border._100}`,
      }}
    >
      <div
        css={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 4,
          color: color.textIcon.title,
        }}
      >
        <span css={{ width: 16, height: 16, display: "inline-flex" }}>
          <BellIcon width={16} height={16} />
        </span>
        <span css={{ fontWeight: fontWeight.bold, fontSize: fontSize.h6 }}>Reminders</span>
      </div>
      <p
        css={{
          color: color.textIcon.secondary,
          fontSize: fontSize.h7,
          lineHeight: 1.5,
          margin: "0 0 14px",
        }}
      >
        Add optional reminders that notify participants before this task expires.
      </p>

      {reminderList.length === 0 && (
        <div
          css={{
            border: `1px dashed ${color.border._160}`,
            borderRadius: 8,
            padding: 14,
            backgroundColor: color.grey_20,
            color: color.textIcon.secondary,
            fontSize: fontSize.h7,
            lineHeight: 1.5,
            marginBottom: 12,
          }}
        >
          No reminders for this task. Participants will not be nudged before it expires.
        </div>
      )}

      {reminderWithLocalKeyList.map(({ reminder, index, reminderKey }) => {
        const unit = getReminderUnit(reminder.secondsBeforeExpiration);
        const value = getReminderValue(reminder.secondsBeforeExpiration, unit);
        const defaultTitle = createDefaultTaskScheduleReminderTitle(
          reminder.secondsBeforeExpiration,
        );
        const previewTitle = reminder.title?.trim() || defaultTitle;
        const previewMessage = reminder.message?.trim() || taskName;
        const inputValue =
          editingReminderTime?.key === reminderKey ? editingReminderTime.value : value;
        const expanded = expandedReminderKey === reminderKey;
        const hasCustomMessage = previewTitle !== defaultTitle || previewMessage !== taskName;
        const error = getReminderError(reminder, index);

        return (
          <div key={reminderKey} css={{ marginBottom: 10 }}>
            <div css={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
              <input
                type="number"
                min={1}
                value={inputValue}
                disabled={isViewOnly}
                onFocus={(e) =>
                  setEditingReminderTime({ key: reminderKey, value: e.currentTarget.value })
                }
                onChange={(e) => {
                  setEditingReminderTime({ key: reminderKey, value: e.target.value });
                  changeReminderTime(index, Number(e.target.value), unit);
                }}
                onBlur={(e) => {
                  setEditingReminderTime(null);
                  changeReminderTime(index, Number(e.currentTarget.value), unit, true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();
                  }
                }}
                css={{
                  width: 56,
                  height: 34,
                  border: `1px solid ${error ? color.warning : color.border._160}`,
                  borderRadius: 6,
                  textAlign: "center",
                  fontSize: fontSize.h6,
                  color: color.textIcon.title,
                  backgroundColor: isViewOnly ? color.surface.disabled : color.white,
                  ":focus": {
                    borderColor: color.primary,
                    outline: "none",
                    boxShadow: `0 0 0 3px ${color.backgroundBlue}`,
                  },
                }}
              />
              <select
                value={unit}
                disabled={isViewOnly}
                onChange={(e) =>
                  changeReminderTime(index, value, e.target.value as ReminderUnit, true)
                }
                css={{
                  height: 34,
                  padding: "0 10px",
                  border: `1px solid ${color.border._160}`,
                  borderRadius: 6,
                  backgroundColor: isViewOnly ? color.surface.disabled : color.white,
                  color: color.textIcon.title,
                  fontSize: fontSize.h6,
                  cursor: isViewOnly ? "not-allowed" : "pointer",
                  ":focus": {
                    borderColor: color.primary,
                    outline: "none",
                    boxShadow: `0 0 0 3px ${color.backgroundBlue}`,
                  },
                }}
              >
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
              </select>
              <span css={{ color: color.textIcon.secondary, fontSize: fontSize.h6 }}>
                before expiration
              </span>

              <div css={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
                <button
                  type="button"
                  disabled={isViewOnly}
                  onClick={() => setExpandedReminderKey(expanded ? null : reminderKey)}
                  css={{
                    background: "none",
                    border: "none",
                    color: color.primary,
                    fontSize: fontSize.h7,
                    fontWeight: fontWeight.bold,
                    cursor: isViewOnly ? "not-allowed" : "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "5px 7px",
                    borderRadius: 6,
                    ":hover": {
                      backgroundColor: color.backgroundBlue,
                    },
                  }}
                >
                  <span css={{ width: 14, height: 14, display: "inline-flex" }}>
                    <EditIcon width={14} height={14} />
                  </span>
                  {expanded
                    ? "Finish Editing"
                    : hasCustomMessage
                      ? "Edit custom message"
                      : "Change message"}
                </button>
                <button
                  type="button"
                  disabled={isViewOnly}
                  onClick={() => removeReminder(index)}
                  css={{
                    width: 30,
                    height: 30,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "none",
                    border: "none",
                    color: color.textIcon.light,
                    borderRadius: 6,
                    cursor: isViewOnly ? "not-allowed" : "pointer",
                    ":hover": {
                      backgroundColor: color.surface.warning,
                      color: color.warning,
                    },
                  }}
                >
                  <DeleteIcon width={16} height={16} fill="currentColor" />
                </button>
              </div>
            </div>

            {error && (
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: color.warning,
                  fontSize: fontSize.h7,
                  marginTop: 6,
                }}
              >
                <span css={{ width: 13, height: 13, display: "inline-flex", flexShrink: 0 }}>
                  <WarningTriangleIcon width={13} height={13} />
                </span>
                {error}
              </div>
            )}

            {expanded && (
              <div
                css={{
                  marginTop: 11,
                  backgroundColor: color.grey_20,
                  border: `1px solid ${color.border._100}`,
                  borderRadius: 8,
                  padding: 14,
                }}
              >
                <div css={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 12 }}>
                  <div>
                    <div
                      css={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                        marginBottom: 5,
                      }}
                    >
                      <label
                        css={{
                          fontSize: fontSize.h7,
                          fontWeight: fontWeight.bold,
                          color: color.textIcon.secondary,
                        }}
                      >
                        Title
                      </label>
                      <span css={{ color: color.textIcon.light, fontSize: fontSize.h8 }}>
                        {previewTitle.length}/{TASK_SCHEDULE_REMINDER_TITLE_MAX_LENGTH}
                      </span>
                    </div>
                    <input
                      type="text"
                      maxLength={TASK_SCHEDULE_REMINDER_TITLE_MAX_LENGTH}
                      value={reminder.title ?? ""}
                      disabled={isViewOnly}
                      placeholder={defaultTitle}
                      onChange={(e) => changeReminderText(index, "title", e.target.value)}
                      css={{
                        width: "100%",
                        height: 38,
                        border: `1px solid ${color.border._160}`,
                        borderRadius: 6,
                        padding: "0 11px",
                        fontSize: fontSize.h6,
                        color: color.textIcon.title,
                        backgroundColor: isViewOnly ? color.surface.disabled : color.white,
                        ":focus": {
                          borderColor: color.primary,
                          outline: "none",
                          boxShadow: `0 0 0 3px ${color.backgroundBlue}`,
                        },
                      }}
                    />
                  </div>
                  <div>
                    <div
                      css={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                        marginBottom: 5,
                      }}
                    >
                      <label
                        css={{
                          fontSize: fontSize.h7,
                          fontWeight: fontWeight.bold,
                          color: color.textIcon.secondary,
                        }}
                      >
                        Subtitle
                      </label>
                      <span css={{ color: color.textIcon.light, fontSize: fontSize.h8 }}>
                        {previewMessage.length}/{TASK_SCHEDULE_REMINDER_MESSAGE_MAX_LENGTH}
                      </span>
                    </div>
                    <input
                      type="text"
                      maxLength={TASK_SCHEDULE_REMINDER_MESSAGE_MAX_LENGTH}
                      value={reminder.message ?? ""}
                      disabled={isViewOnly}
                      placeholder={taskName}
                      onChange={(e) => changeReminderText(index, "message", e.target.value)}
                      css={{
                        width: "100%",
                        height: 38,
                        border: `1px solid ${color.border._160}`,
                        borderRadius: 6,
                        padding: "0 11px",
                        fontSize: fontSize.h6,
                        color: color.textIcon.title,
                        backgroundColor: isViewOnly ? color.surface.disabled : color.white,
                        ":focus": {
                          borderColor: color.primary,
                          outline: "none",
                          boxShadow: `0 0 0 3px ${color.backgroundBlue}`,
                        },
                      }}
                    />
                  </div>
                </div>

                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    marginBottom: 10,
                  }}
                >
                  <span
                    css={{ color: color.textIcon.light, fontSize: fontSize.h8, lineHeight: 1.45 }}
                  >
                    Plain text only. A custom message will not update if you rename the task.
                  </span>
                  {hasCustomMessage && (
                    <button
                      type="button"
                      disabled={isViewOnly}
                      onClick={() => resetReminderText(index)}
                      css={{
                        background: "none",
                        border: "none",
                        color: color.primary,
                        fontSize: fontSize.h7,
                        fontWeight: fontWeight.bold,
                        cursor: isViewOnly ? "not-allowed" : "pointer",
                        whiteSpace: "nowrap",
                        padding: "2px 4px",
                      }}
                    >
                      Reset to default
                    </button>
                  )}
                </div>

                <div
                  css={{
                    fontSize: fontSize.h8,
                    fontWeight: fontWeight.bold,
                    color: color.textIcon.light,
                    marginBottom: 6,
                  }}
                >
                  Preview
                </div>
                <div
                  css={{
                    backgroundColor: color.white,
                    border: `1px solid ${color.border._100}`,
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                    boxShadow: "0 1px 2px rgba(15,23,42,.05)",
                  }}
                >
                  <img
                    src={LabfrontIcon}
                    alt="Labfront"
                    css={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0 }}
                  />
                  <div css={{ minWidth: 0, flex: 1 }}>
                    <div
                      css={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        gap: 8,
                      }}
                    >
                      <span
                        css={{
                          fontSize: fontSize.h8,
                          fontWeight: fontWeight.bold,
                          color: color.textIcon.light,
                        }}
                      >
                        Labfront
                      </span>
                      <span css={{ fontSize: fontSize.h7, color: color.textIcon.light }}>now</span>
                    </div>
                    <div
                      css={{
                        fontSize: fontSize.h6,
                        fontWeight: fontWeight.thick,
                        color: color.textIcon.title,
                        lineHeight: 1.3,
                        wordBreak: "break-word",
                      }}
                    >
                      {previewTitle}
                    </div>
                    <div
                      css={{
                        fontSize: fontSize.h6,
                        color: color.textIcon.secondary,
                        lineHeight: 1.35,
                        wordBreak: "break-word",
                      }}
                    >
                      {previewMessage}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {reminderList.length > 0 && (
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            color: color.textIcon.secondary,
            fontSize: fontSize.h7,
            margin: "2px 0 12px",
          }}
        >
          <span css={{ width: 13, height: 13, display: "inline-flex", flexShrink: 0 }}>
            <InfoIcon width={13} height={13} />
          </span>
          {reminderList.length} reminder{reminderList.length > 1 ? "s" : ""} will fire before this
          task expires.
        </div>
      )}

      {canAdd && (
        <button
          type="button"
          onClick={addReminder}
          css={{
            height: 36,
            padding: "0 13px",
            borderRadius: 8,
            border: `1px dashed ${color.border._160}`,
            backgroundColor: color.white,
            color: color.primary,
            fontWeight: fontWeight.bold,
            fontSize: fontSize.h7,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            ":hover": {
              borderColor: color.primary,
              backgroundColor: color.backgroundBlue,
            },
          }}
        >
          <span css={{ fontSize: fontSize.h5, lineHeight: 1 }}>+</span>
          Add reminder
        </button>
      )}

      {atCap && (
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            color: color.textIcon.light,
            fontSize: fontSize.h7,
          }}
        >
          <span css={{ width: 14, height: 14, display: "inline-flex" }}>
            <InfoIcon width={14} height={14} />
          </span>
          Maximum of {TASK_SCHEDULE_REMINDER_MAX_COUNT} reminders reached.
        </div>
      )}
    </div>
  );
};
